import { FunctionComponent, useDeferredValue, useMemo, useState, useEffect, memo } from 'react';
import { ICoordinatingGroup, ICountry, IInstitution, IMasterGroup, ISourceDataConfigurationSummary, ISourceDataSummary, ITrial, SourceDataVerificationPatient } from '@/api/dtos';
import { Divider, List, LinearProgress, Box, styled } from '@mui/material'
import PatientDataListItemSkeleton from './PatientDataListItemSkeleton';
import { Virtuoso } from 'react-virtuoso';
import { BasicAlert } from '@ngt/forms';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import PatientDataListProcessor, { IPatientDataItem, PatientDataType } from '@/utilities/data/PatientDataListProcessor';
import PatientDataListConfiguration from './PatientDataListConfiguration';

const ProgressContainer = styled('div')({
    position: 'relative'
});

const DataProgress = styled(LinearProgress)({
    position: 'absolute',
    left: 0,
    right: 0
});

interface IPatientDataList {
    trial?: ITrial;
    patient?: SourceDataVerificationPatient;
    configurations?: ISourceDataConfigurationSummary[]
    loading?: boolean;
    updating?: boolean;
    skeletonCount?: number;
}


// Item contents are cached properly with React.memo
const InnerItem = memo(({ index, item, context}: {index: number; item: IPatientDataItem; context: IVirtuosoContext}) => {
    if (item.type === PatientDataType.Configuration) {
        return ( 
            <PatientDataListConfiguration 
                {...item}
                {...context}
            />
        )
    }

    return (
        <PatientDataListItemSkeleton />
    )
})

interface IVirtuosoContext { 
    trial?: ITrial;
    patient?: SourceDataVerificationPatient;
    refreshSummaries?: () => Promise<void>;
}

// The callback is executed often - don't inline complex components in here.
const virtuosoItemContent = (index: number, item: IPatientDataItem, context: IVirtuosoContext) => {
    return <InnerItem index={index} item={item} context={context} />
}

const PatientDataList: FunctionComponent<IPatientDataList> = ({
    patient,
    configurations,
    trial,
    loading,
    updating,
    skeletonCount
}) => {
    const loadingArray = useMemo(() => {
        return Array(skeletonCount ?? 20).fill(0);
    }, [skeletonCount])

    const processor = useMemo(() => {
        return new PatientDataListProcessor();
    }, []);

    
    const [processedList, setProcessedList] = useState<IPatientDataItem[]>([])

    useEffect(() => {
        const processEvents = async () => {
            const items = await processor.processAll(configurations);
            setProcessedList(items);
        }

        processEvents();
    }, [processor, configurations, setProcessedList]);

    const context = useMemo(() => ({patient, trial}), [patient, trial])

    return (
        <>
            {
                updating && (
                    <ProgressContainer>
                        <DataProgress
                            color="secondary"
                        />
                    </ProgressContainer>
                )
            }
            <List disablePadding>
                {
                    (loading || !!processedList?.length) && (
                        <Virtuoso
                            context={context}
                            useWindowScroll
                            data={loading ? loadingArray : processedList}
                            itemContent={virtuosoItemContent}
                        />
                    )
                }
                {
                    (!loading && !processedList?.length) && (
                        <BasicAlert
                            title="No Participant Data"
                            description="No Participant Data has been added to the selected participant."
                            severity="warning"
                            icon={faExclamationTriangle}
                        />
                    )
                }
            </List>
        </>
    );
}

export default PatientDataList;